import {
  ActiveInactiveStatus,
  ApiTokenListResponse,
  CreditSpentItem,
  CreditTopUpItem,
  MembershipType,
  SMSPayload,
  SMSSendingResponse,
  SmsSettingsDTO,
  StripeProductItem,
  TokenPageableRequestParams,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

export type SmsApiType = ReturnType<typeof smsApi>;

const smsApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;

  return {
    sendSMSToMember({
      body,
      userMemberId,
    }: {
      body: string;
      userMemberId: string;
    }) {
      return axiosInstance.post(
        `${clubPrefix}user-member-send-sms/${userMemberId}`,
        { body },
      );
    },
    sendSMSToMembers({
      smsPayload,
      userMemberIds,
    }: {
      smsPayload: SMSPayload;
      userMemberIds: string[];
    }) {
      return axiosInstance.post<SMSSendingResponse>(
        `${clubPrefix}user-member-send-sms/no-filter`,
        {
          smsPayload,
          userMemberIds,
        },
      );
    },
    sendSMSToMembersFilter(params: {
      smsPayload: SMSPayload;
      userMemberIdsToExclude?: string[];
      createdFrom?: string;
      createdTo?: string;
      smsCommunication?: boolean;
      emailCommunication?: boolean;
      membershipIdList?: number[];
      membershipStatusList?: ActiveInactiveStatus[];
      membershipTypeList?: MembershipType[];
      membershipStartFrom?: string;
      membershipStartTo?: string;
      membershipCancellationFrom?: string;
      membershipCancellationTo?: string;
      membershipExpireFrom?: string;
      membershipExpireTo?: string;
      assignedStaffIdList?: string[];
      creditsRemainingFrom?: string;
      creditsRemainingTo?: string;
    }) {
      return axiosInstance.post<SMSSendingResponse>(
        `${clubPrefix}user-member-send-sms/filter`,
        params,
      );
    },
    sendSMSToLead({ leadId, body }: { leadId: number; body: string }) {
      return axiosInstance.post<SMSSendingResponse>(
        `${clubPrefix}lead-send-sms/${leadId}`,
        {
          body,
        },
      );
    },
    sendSMSToLeads({
      smsPayload,
      leadIds,
    }: {
      smsPayload: SMSPayload;
      leadIds: number[];
    }) {
      return axiosInstance.post<SMSSendingResponse>(
        `${clubPrefix}lead-send-sms/no-filter`,
        {
          smsPayload,
          leadIds,
        },
      );
    },
    sendSMSToLeadsFilter(params: {
      smsPayload: SMSPayload;
      leadIdsToExclude?: number[];
      createdFrom?: string;
      createdTo?: string;
      smsCommunication?: boolean;
      emailCommunication?: boolean;
      leadStatusIdList?: number[];
      leadSourceIdList?: number[];
    }) {
      return axiosInstance.post<SMSSendingResponse>(
        `${clubPrefix}lead-send-sms/filter`,
        params,
      );
    },
    getCreditsHistoryTopUps({
      limit,
      nextPageToken,
    }: TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<CreditTopUpItem>>(
        `${clubPrefix}messaging-history/credits/top-ups`,
        {
          params: { limit, nextPageToken },
        },
      );
    },
    getCreditsHistorySpent({
      limit,
      nextPageToken,
    }: TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<CreditSpentItem>>(
        `${clubPrefix}messaging-history/credits/spent`,
        {
          params: { limit, nextPageToken },
        },
      );
    },
    getStripeProducts() {
      return axiosInstance.get<StripeProductItem[]>(
        `${clubPrefix}stripe-product`,
      );
    },
    smsSettings() {
      return axiosInstance.get<SmsSettingsDTO>(`${clubPrefix}sms-settings`);
    },
  };
};

export default smsApi;
